<template>
  <popover
    :target="target"
    triggers="hover"
    custom-class="entity-popover"
    placement="top"
    @shown="onShown"
    boundary="viewport"
    :show-delay="1000"
  >
    <div class="popover-container">
      <div class="flex-center" v-if="isLoading">
        <spinner />
      </div>
      <div
        class="d-flex"
        v-if="
          (entityType === ENTITY_TYPE.KEY_RESULT ||
            entityType === ENTITY_TYPE.INITIATIVE ||
            entityType === ENTITY_TYPE.TASK) &&
            goalObj
        "
      >
        <i class="goal-icon flex-shrink-0"></i>
        <div class="ml-2">
          <router-link :to="goalPageLocation" class="text-medium text-primary" v-no-capture>
            {{ goalObj.title }}
          </router-link>
          <goal-meta :goal="goalObj" />
        </div>
      </div>
      <div
        class="d-flex"
        v-if="
          (entityType === ENTITY_TYPE.INITIATIVE || entityType === ENTITY_TYPE.TASK) &&
            (krObj || project)
        "
      >
        <i class="kr-icon flex-shrink-0" v-if="krObj"></i>
        <project-icon class="project-icon" :color="project.color" v-else-if="project" />
        <div class="ml-2">
          <router-link
            :to="krPageLocation"
            class="text-medium text-primary"
            v-no-capture
            v-if="krObj"
          >
            {{ krObj.title }}
          </router-link>
          <router-link
            :to="projectPageLocation"
            class="text-medium text-primary"
            v-no-capture
            v-else-if="project"
          >
            {{ project.title }}
          </router-link>
        </div>
      </div>
      <div class="d-flex" v-if="entityType === ENTITY_TYPE.TASK && parentInitiativeObj">
        <initiative-icon :is-done-and-closed="parentInitiativeObj.isDoneAndClosed" />
        <div class="ml-2">
          <router-link :to="initiativePageLocation" class="text-medium text-primary" v-no-capture>
            {{ parentInitiativeObj.title }}
          </router-link>
        </div>
      </div>
    </div>
  </popover>
</template>

<script>
import GoalMeta from '@/components/goal/GoalMeta.vue'
import InitiativeIcon from '@/components/initiative/InitiativeIcon'
import ProjectIcon from '@/components/project/ProjectIcon.vue'
import Spinner from '@/components/Spinner'
import { ENTITY_TYPE } from '@/constants/entities'
import Popover from '@/components/Popover'
import {
  getGoalPageRouteConfig,
  getInitiativePageRouteConfig,
  getKrPageRouteConfig,
  getProjectPageRouteConfig
} from '@/utils/navigationConfigs'

// TODO: refactor this component to recursively find parent. Doing so will remove existing custom checks.

/**
 * This shows goalTitle, krTitle in a popover.
 * This can be typically used in places where we want to show goal and kr title on hover of initiative title
 */
export default {
  name: 'EntityPopover',
  props: [
    'target',
    'entityType',
    'goalId',
    'krId',
    'orgId',
    'goal',
    'kr',
    'parentInitiativeId',
    'project'
  ],
  components: {
    ProjectIcon,
    GoalMeta,
    Popover,
    Spinner,
    InitiativeIcon
  },
  data() {
    return {
      ENTITY_TYPE,
      isLoading: false
    }
  },
  computed: {
    parentInitiativeObj() {
      return this.$store.getters.initiativeById(this.parentInitiativeId)
    },
    goalObj() {
      return this.goal || this.$store.getters.goalById(this.goalId)
    },
    krObj() {
      return this.kr || this.$store.getters.keyResultById(this.krId)
    },
    goalPageLocation() {
      return getGoalPageRouteConfig({
        orgId: this.goalObj?.organizationId,
        goalId: this.goalObj?.id
      })
    },
    krPageLocation() {
      return getKrPageRouteConfig({
        orgId: this.krObj?.organizationId,
        krId: this.krObj?.id
      })
    },
    projectPageLocation() {
      return getProjectPageRouteConfig({
        orgId: this.project.organizationId,
        projectId: this.project.id
      })
    },
    initiativePageLocation() {
      return getInitiativePageRouteConfig({
        orgId: this.krObj?.organizationId,
        initiativeId: this.parentInitiativeId
      })
    }
  },
  methods: {
    async onShown() {
      this.isLoading = true
      if (this.goalId && !this.goalObj) {
        await this.$store.dispatch('getGoal', {
          orgId: this.orgId,
          goalId: this.goalId
        })
      } else if (this.krId && !this.krObj) {
        await this.$store.dispatch('getKR', {
          orgId: this.orgId,
          krId: this.krId
        })
      }
      if (this.parentInitiativeId && !this.parentInitiativeObj) {
        this.$store.dispatch('getInitiative', {
          orgId: this.orgId,
          initiativeId: this.parentInitiativeId
        })
      }
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.popover-container {
  min-width: 220px;
  min-height: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  > div {
    position: relative;

    &:nth-child(1):not(:last-child) {
      margin-top: 8px;

      &:before {
        content: ' ';
        display: block;
        width: 1px;
        background: $borderTertiary;
        position: absolute;
        left: 7px;
        top: 18px;
        bottom: -20px;
      }
    }

    &:nth-child(2) {
      margin-left: 23px;
      margin-top: 8px;

      &:before {
        content: ' ';
        display: block;
        height: 1px;
        width: 10px;
        background: $borderTertiary;
        position: absolute;
        left: -16px;
        top: 11px;
      }
      &:not(:last-child):after {
        content: ' ';
        display: block;
        width: 1px;
        background: $borderTertiary;
        position: absolute;
        left: 3px;
        top: 18px;
        bottom: -20px;
      }
    }
    &:nth-child(3) {
      margin-left: 40px;
      margin-top: 8px;

      &:before {
        content: ' ';
        display: block;
        height: 1px;
        width: 7px;
        background: $borderTertiary;
        position: absolute;
        left: -13px;
        top: 11px;
      }
    }
  }
}

.goal-icon {
  margin-top: 3px;
}

.kr-icon {
  margin-top: 5px;
}
.project-icon {
  height: 20px;
  flex-shrink: 0;
}
</style>
<style lang="scss">
.entity-popover .popover-body {
  padding: 12px 16px;
}
</style>
